import {
  TestCase,
  TestCaseParameter,
  TestCaseTemplate,
  TransformTestCase
} from "../types/testCaseTemplateTypes";
import {
  ParametersConstants,
  RequestParametersConstants
} from "../constants/parameters.constants";
import { TransformExecuteResponse } from "../types/clientTypes";
import {
  Environment, EnvironmentHeader,
  Server
} from "../types/environmentTypes";
import { v4 as uuid } from 'uuid'
import { SelectedVariable } from "../types/variableType";
import { Header } from "../types/sharedTypes";
import {
  customParse,
  customStringify
} from "./getStringJson";

const variableKey = "variableId_"

export const getServer = (testCaseTemplate: TestCaseTemplate, environment: Environment): Server | undefined => {
  const environmentServer = testCaseTemplate.environmentServers.find(item => item.environmentId === environment.id);
  return environment.servers.find(item => item.id === environmentServer?.serverId);
}

export const getTemplateAndTestCaseByTestCaseId = (templates: Array<TestCaseTemplate>, testCaseId: number): { testCase: TestCase, template: TestCaseTemplate } | undefined => {
  let testCase: TestCase | undefined;
  let template: TestCaseTemplate | undefined;
   templates.some(testCaseTemplate => {
     testCase = testCaseTemplate.childTestCases.find(testCase => testCase.id === testCaseId);
     if (!!testCase) {
       template = testCaseTemplate;
       return true;
     }
   });
   if (!!testCase && !!template) {
     return { template, testCase };
   }
}

export const getTransformTestCase = (variables: Array<SelectedVariable>, testCaseTemplate: TestCaseTemplate, testCase: TestCase): TransformTestCase => {
  return  {
    ...testCase,
    payload: transformField(variables, testCase.requestParameterValues, testCaseTemplate.payload),
    body: transformField(variables, testCase.requestParameterValues, testCaseTemplate.body),
    endpoint: transformField(variables, testCase.requestParameterValues, testCaseTemplate.endpoint),
    expectedBody: transformField(variables, testCase.responseParameterValues, testCaseTemplate.expectedBody),
    requestHeaders: testCaseTemplate.requestHeaders.map(item => ({
      ...item,
      value: transformField(variables, testCase.requestParameterValues, item.value)
    }))
  };
}

export const getRequestHeaders = (variables: Array<SelectedVariable>, testCase: TransformTestCase, environmentHeaders: Array<EnvironmentHeader>): Array<Header> => {
  const result: Array<Header> = [];
  const testCaseHeaders = testCase.requestHeaders;
  const transformedEnvironmentHeaders: Array<EnvironmentHeader> = environmentHeaders.map(item => ({
    ...item,
    value: transformFieldByVariables(variables, item.value)
  }))

  transformedEnvironmentHeaders.forEach(environmentHeader => {
    const testCaseHeader = testCaseHeaders?.find(testCaseHeader => testCaseHeader.name === environmentHeader.name);
    if (!testCaseHeader) {
      result.push({ name: environmentHeader.name, value: environmentHeader.value })
    }
  });

  testCaseHeaders?.forEach(testCaseHeader => {
    if (!!testCaseHeader.value) {
      result.push({ name: testCaseHeader.name, value: testCaseHeader.value });
    }
  });

  return result;
}

export const transformTemplateForUser = (variables: Array<SelectedVariable>, template?: TestCaseTemplate): TestCaseTemplate | undefined => {
  if (!!template) {
    return {
      ...template,
      payload: transformFieldForUser(variables, template.payload),
      body: transformFieldForUser(variables, template.body),
      endpoint: transformFieldForUser(variables, template.endpoint),
      expectedBody: transformFieldForUser(variables, template.expectedBody),
      requestHeaders: template.requestHeaders.map(item => ({
        ...item,
        value: transformFieldForUser(variables, item.value)
      }))
    }
  } else {
    return template;
  }
}

export const transformTemplateForBackend = (variables: Array<SelectedVariable>, template: TestCaseTemplate): TestCaseTemplate => {
  return {
    ...template,
    payload: transformFieldForBackend(variables, template.payload),
    body: transformFieldForBackend(variables, template.body),
    endpoint: transformFieldForBackend(variables, template.endpoint),
    expectedBody: transformFieldForBackend(variables, template.expectedBody),
    requestHeaders: template.requestHeaders.map(item => ({
      ...item,
      value: transformFieldForBackend(variables, item.value)
    }))
  }
}

export const getTransformTestCaseByExecuteResult = (variables: Array<SelectedVariable>, testCase: TransformTestCase, executeResponse: TransformExecuteResponse): TransformTestCase => {
  return  {
    ...testCase,
    expectedBody: getTransformTestCaseFieldByResponse(variables, testCase.expectedBody, executeResponse.responseBody)
  }
}

export const getTransformTestCaseFieldByResponse = (variables: Array<SelectedVariable>, field?: string, responseField?: string): string => {
  if (!!field) {
    try {
      const responseBody = customParse(responseField as string);
      if (responseField) {
        if (field === `<<${ParametersConstants.any}>>`
          || Array.isArray(responseBody) && field === `<<${ParametersConstants.array}>>`
          || typeof responseBody === 'object' && field === `<<${ParametersConstants.object}>>`) {
          return responseField;
        }
      }

      const transformedField = transformFieldWithStaticProps(variables, field);
      const expectedBody = customParse(transformedField);
      transformObject(responseBody, expectedBody);
      return customStringify(expectedBody);
    } catch (e) {
      return field;
    }
  } else {
    return "";
  }
}

const transformObject = (responseBody: any, expectedBody: any): void => {
  for (let key in responseBody) {
    if (expectedBody[key] === `<<${ParametersConstants.any}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (typeof responseBody[key] === 'string' && expectedBody[key] === `<<${ParametersConstants.string}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (responseBody[key] === null && expectedBody[key] === `<<${ParametersConstants.null}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (responseBody[key] !== null && expectedBody[key] === `<<${ParametersConstants.notNull}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (!isNaN(Date.parse(responseBody[key])) && expectedBody[key] === `<<${ParametersConstants.date}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if ((Number.isInteger(responseBody[key]) || typeof responseBody[key] === 'bigint') && expectedBody[key] === `<<${ParametersConstants.integer}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (((typeof responseBody[key] !== 'bigint' && responseBody[key] % 1) || Number.isInteger(responseBody[key])) && expectedBody[key] === `<<${ParametersConstants.decimal}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (Array.isArray(responseBody[key]) && expectedBody[key] === `<<${ParametersConstants.array}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (isValidGUID(responseBody[key]) && expectedBody[key] === `<<${ParametersConstants.guid}>>`) {
      expectedBody[key] = responseBody[key];
      continue;
    }
    if (typeof responseBody[key] === 'object' && responseBody[key] !== null) {
      if (expectedBody[key] === `<<${ParametersConstants.object}>>`) {
        expectedBody[key] = responseBody[key];
      } else if (typeof expectedBody[key] === 'object' && expectedBody[key] !== null) {
        transformObject(responseBody[key], expectedBody[key]);
      }
    }
  }
}

const isValidGUID = (str: any): boolean => {
  let regex = new RegExp(/^[{]?[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}[}]?$/);

  if (str == null && !(typeof str === 'string' || str instanceof String)) {
    return false;
  }

  return regex.test(str);
}

const transformFieldByVariables = (variables: Array<SelectedVariable>, field: string): string => {
  let result = field;


  variables.forEach(variable => {
    const value = variable.value.value;
    result = result.replaceAll(`<{${variableKey}${variable.id}}>`, value);
  });

  return result;
}

const transformField = (variables: Array<SelectedVariable>, parameters: Array<TestCaseParameter>, field?: string): string => {
  let result = field || "";

  parameters.forEach(parameter => {
    result = result.replaceAll(`{{${parameter.parameterName}}}`, parameter.value || "")
  });

  result = transformFieldByVariables(variables, result);

  const nowDate = new Date();
  let newDate = new Date();
  result = result.replaceAll(`<<${RequestParametersConstants.day}>>`, ('0' + nowDate.getDate()).slice(-2));

  newDate.setDate(nowDate.getDate() + 1);
  result = result.replaceAll(`<<${RequestParametersConstants.dayInc}>>`, ('0' + newDate.getDate()).slice(-2));

  newDate.setDate(nowDate.getDate() - 1);
  result = result.replaceAll(`<<${RequestParametersConstants.deyDec}>>`, ('0' + newDate.getDate()).slice(-2));

  newDate = new Date();
  result = result.replaceAll(`<<${RequestParametersConstants.month}>>`, ('0' + (nowDate.getMonth() + 1)).slice(-2));

  newDate.setMonth(nowDate.getMonth() + 1);
  result = result.replaceAll(`<<${RequestParametersConstants.monthInc}>>`, ('0' + (newDate.getMonth() + 1)).slice(-2));

  newDate.setMonth(nowDate.getMonth() - 1);
  result = result.replaceAll(`<<${RequestParametersConstants.monthDec}>>`, ('0' + (newDate.getMonth() + 1)).slice(-2));

  newDate = new Date();
  result = result.replaceAll(`<<${RequestParametersConstants.year}>>`, nowDate.getFullYear().toString());

  newDate.setFullYear(nowDate.getFullYear() + 1);
  result = result.replaceAll(`<<${RequestParametersConstants.yearInc}>>`, newDate.getFullYear().toString());

  newDate.setFullYear(nowDate.getFullYear() - 1);
  result = result.replaceAll(`<<${RequestParametersConstants.yearDec}>>`, newDate.getFullYear().toString());

  let searchIndex = result.indexOf(`<<${RequestParametersConstants.uuid}>>`);
  while (searchIndex > -1) {
    result = result.replace(`<<${RequestParametersConstants.uuid}>>`, uuid());
    searchIndex = result.indexOf(`<<${RequestParametersConstants.uuid}>>`);
  }

  searchIndex = result.indexOf(`<<${RequestParametersConstants.uniqueNumber}>>`);
  const uniqueNumbers: Array<string> = []
  while (searchIndex > -1) {
    let uniqueNumber = Date.now() + (Math.random() * 1000).toFixed();
    while (uniqueNumbers.includes(uniqueNumber)) {
      uniqueNumber = Date.now() + (Math.random() * 1000).toFixed();
    }
    uniqueNumbers.push(uniqueNumber);
    result = result.replace(`<<${RequestParametersConstants.uniqueNumber}>>`, uniqueNumber);
    searchIndex = result.indexOf(`<<${RequestParametersConstants.uniqueNumber}>>`);
  }

  return result;
}

const transformFieldWithStaticProps = (variables: Array<SelectedVariable>, field?: string): string => {
  let result = field || "";
  Object.values(ParametersConstants).forEach(params => {
    result = result.replaceAll(`<<${params}>>`, `"<<${params}>>"`)
  });
  variables.forEach(variable => {
    result = result.replaceAll(`<{${variableKey}${variable.id}}>`, `"${variable.value.value}"`);
  });
  return result;
}

export const transformFieldForUser = (variables: Array<SelectedVariable>, field?: string): string => {
  let result = field || "";

  variables.forEach(variable => {
    result = result.replaceAll(`<{${variableKey}${variable.id}}>`, `<{${variable.name}}>`)
  });

  return result;
}

export const transformFieldForBackend = (variables: Array<SelectedVariable>, field?: string): string => {
  let result = field || "";

  variables.forEach(variable => {
    result = result.replaceAll(`<{${variable.name}}>`, `<{${variableKey}${variable.id}}>`)
  });

  return result;
}
